import { capitalizeString } from '@strata/helper-functions';
import { useEffect, useState } from 'react';

import { handleError } from './ErrorManagement';
import { useGet } from './Http';

export const ListType = {
    account: 'account',
    group: 'group',
    network: 'network',
    prescriber: 'prescriber',
    product: 'product',
    supplier: 'supplier',
    therapeutic_category: 'therapeutic_category',
    zip_code: 'zip_code',
};

/** Display names for each ListType */
export const ListTypeNameMap = Object.fromEntries(
    Object.values(ListType).map((type) => [
        type,
        type === ListType.supplier ? 'Pharmacy' : capitalizeString(type.replace('_', ' ')),
    ]),
);

/** Service schemas that leverage lists */
export const ListSchema = {
    assignment: 'assignment',
    finance: 'finance',
};

/** App route prefix for each list schema */
export const ListSchemaAppRoutePrefix = {
    [ListSchema.assignment]: '/route-management/lists',
    [ListSchema.finance]: '/program-management/lists',
};

/** Api route prefix for each list schema */
export const ListSchemaApiURLPrefix = {
    [ListSchema.assignment]: `/${ListSchema.assignment}/routing_lists`,
    [ListSchema.finance]: `/${ListSchema.finance}/lists`,
};

/** Applicable list types for each schema */
export const ListTypesForSchema = {
    [ListSchema.assignment]: [
        ListType.account,
        ListType.group,
        ListType.network,
        ListType.prescriber,
        ListType.product,
        ListType.therapeutic_category,
        ListType.zip_code,
    ],
    [ListSchema.finance]: [ListType.supplier, ListType.product],
};

/** Gets display name for a ListType's list item */
export const getListItemName = (listType) => {
    switch (listType) {
        case ListType.network:
        case ListType.supplier:
            return 'pharmacy';
        case ListType.zip_code:
            return 'ZIP code';
        default:
            return ListTypeNameMap[listType].toLowerCase();
    }
};

/** Array of keys in a list item for each ListType */
export const ListTypeItemKeyMap = {
    [ListType.account]: ['account'],
    [ListType.group]: ['benefit_group'],
    [ListType.network]: ['organization_name', 'supplier_npi'],
    [ListType.prescriber]: ['prescriber_name', 'prescriber_npi'],
    [ListType.product]: ['product_service_name', 'product_service_oid', 'offer_rate'],
    [ListType.supplier]: ['organization_name', 'parent_pharmacy_npi'],
    [ListType.therapeutic_category]: ['therapeutic_category'],
    [ListType.zip_code]: ['zip_code'],
};

/** Array of list detail column names for each ListType */
export const ListItemColumnNames = {
    [ListType.account]: [ListTypeNameMap[ListType.account]],
    [ListType.group]: ['Group ID'],
    [ListType.network]: ['Pharmacy Name', 'NPI'],
    [ListType.prescriber]: ['Prescriber Name', 'NPI'],
    [ListType.product]: ['Product', 'OID', 'Offer Rate'],
    [ListType.supplier]: ['Pharmacy Name', 'NPI'],
    [ListType.therapeutic_category]: [ListTypeNameMap[ListType.therapeutic_category]],
    [ListType.zip_code]: [ListTypeNameMap[ListType.zip_code]],
};

/** Checks if two items of a given listType are equal */
export const areListItemsEqual = (a, b, listType) =>
    !ListTypeItemKeyMap[listType].some((key) => a[key] !== b[key]);

/** Returns listToFilter with any items present in itemsToRemove removed */
export const filterListItems = (listToFilter, itemsToRemove, listType) =>
    listToFilter.filter(
        (item) =>
            !itemsToRemove.find((itemToDelete) => areListItemsEqual(itemToDelete, item, listType)),
    );

export const pluralizeListTypeName = (name) =>
    name?.endsWith('y') ? `${name.replace(/y$/, '')}ies` : `${name}s`;

/** Validate string of NPIs entered in a text area */
export const validateNpis = (values) => {
    // Check for invalid characters
    if (/[^\d\s,]/.test(values.npis)) {
        handleError('NPIs formatted incorrectly.');
        return null;
    }

    // Split, trim, & dedupe NPIs
    const npisToAdd = [
        ...new Set(
            (values.npis?.split(/[\r\n,]/) ?? []).map((item) => item.trim()).filter((item) => item),
        ),
    ];

    // Check for NPIs with length !== 10
    const invalidNpi = npisToAdd.find((npi) => npi.length !== 10);
    if (invalidNpi) {
        handleError(`Incorrect length for NPI "${invalidNpi}".`);
        return null;
    }

    // All NPIs valid
    return npisToAdd;
};

/** Hook to return an array of configured lists for use in a select component */
export const useListTypeOptions = (listSchema, listType) => {
    const [listOptions, setListOptions] = useState();
    const lists = useGet(`${ListSchemaApiURLPrefix[listSchema]}/${listType}`);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setListOptions(
            lists?.map((list) => ({
                name: list.list_name,
                secondaryData: list.list_description,
                value: list[`${listType}_list_id`],
            })),
        );
    }, [listType, lists]);
    return listOptions;
};
